import React, { useState, useEffect, useRef } from "react";
import PasswordField from "../../common/passwordInput";
import { Box, Typography, CircularProgress } from "@mui/material";
import validator from "validator";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { resetUser } from "../../../store/features/loginUser";
import { resetAgentUser } from "../../../store/features/loginUserTenant";
import { clearSessionScopedState } from "../../../utils/clearSessionScopedState";
import {
  updatePassword,
  resetUpdatePasswordState,
} from "../../../store/features/updatePassword";
import "./securityToast.css";

const REDIRECT_AFTER_MS = 3200;
const TOAST_AUTO_CLOSE_MS = 9000;

function splitPasswordSuccessMessage(message) {
  const dotSpace = message.indexOf(". ");
  if (dotSpace === -1) {
    return { primary: message, secondary: null };
  }
  return {
    primary: message.slice(0, dotSpace + 1).trim(),
    secondary: message.slice(dotSpace + 2).trim(),
  };
}

function PasswordUpdateSuccessToast({ message }) {
  const { primary, secondary } = splitPasswordSuccessMessage(message);
  return (
    <div className="password-update-toast-inner">
      <p className="password-update-toast-primary">{primary}</p>
      {secondary ? (
        <p className="password-update-toast-secondary">{secondary}</p>
      ) : null}
    </div>
  );
}

const strongPasswordOptions = {
  minLength: 8,
  minLowercase: 1,
  minUppercase: 1,
  minNumbers: 1,
  minSymbols: 1,
};

function Security() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const redirectTimerRef = useRef(null);
  const loading = useSelector((state) => state.updatePassword?.loading);

  useEffect(
    () => () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    },
    [],
  );

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const validate = () => {
    const next = { oldPassword: "", newPassword: "", confirmPassword: "" };
    let valid = true;

    if (!oldPassword.trim()) {
      next.oldPassword = "Current password is required.";
      valid = false;
    }

    if (!newPassword) {
      next.newPassword = "New password is required.";
      valid = false;
    } else if (!validator.isStrongPassword(newPassword, strongPasswordOptions)) {
      next.newPassword =
        "Use at least 8 characters with uppercase, lowercase, number, and symbol.";
      valid = false;
    }

    if (!confirmPassword) {
      next.confirmPassword = "Please confirm your new password.";
      valid = false;
    } else if (newPassword !== confirmPassword) {
      next.confirmPassword = "New password and confirmation do not match.";
      valid = false;
    }

    setErrors(next);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    try {
      const result = await dispatch(
        updatePassword({
          oldPassword,
          newPassword,
          confirmPassword,
        }),
      ).unwrap();

      const successMessage =
        result?.message ||
        "Password updated successfully. All sessions have been cleared; please log in again.";

      toast.success(<PasswordUpdateSuccessToast message={successMessage} />, {
        toastId: "password-update-success",
        className: "toast-password-update-success",
        autoClose: TOAST_AUTO_CLOSE_MS,
        closeOnClick: true,
      });

      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({ oldPassword: "", newPassword: "", confirmPassword: "" });
      dispatch(resetUpdatePasswordState());

      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
      redirectTimerRef.current = setTimeout(() => {
        redirectTimerRef.current = null;
        toast.dismiss("password-update-success");
        dispatch(resetUser());
        dispatch(resetAgentUser());
        clearSessionScopedState();
        localStorage.removeItem("auth_token");
        localStorage.setItem("logout", Date.now());
        navigate("/login", { replace: true });
      }, REDIRECT_AFTER_MS);
    } catch (err) {
      const message =
        err?.message ||
        (typeof err === "string" ? err : "Could not update password.");
      toast.error(message);
    }
  };

  return (
    <div
      className="profile-main-container"
      style={{ width: "100%", minWidth: 0, boxSizing: "border-box" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          p: { xs: 2, md: 3 },
          gap: { xs: 4, md: 2 },
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: "100%", md: 300 },
            minWidth: 0,
            flexShrink: { md: 0 },
          }}
          className="Personal-Info-Text-Container"
        >
          <Typography
            sx={{
              color: "#061522",
              fontFamily: "Inter",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 500,
              lineHeight: "22px",
              mb: 1,
            }}
          >
            Password & Security
          </Typography>
          <Typography
            sx={{
              color: "#405261",
              fontFamily: "Inter",
              fontSize: { xs: "13px", md: "14px" },
              fontWeight: 400,
              lineHeight: "22px",
            }}
          >
            Protect your account with strong credentials and access controls
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            flex: { md: 1 },
            minWidth: 0,
            width: "100%",
            maxWidth: { xs: "100%", md: 552 },
            pr: { xs: 0, md: 2, lg: 4, xl: 7 },
            boxSizing: "border-box",
          }}
        >
          <div className="Spacing-Bottom">
            <PasswordField
              title="Old Password"
              placeholder="Enter old password"
              name="oldPassword"
              id="oldPassword"
              value={oldPassword}
              onChange={(e) => {
                setOldPassword(e.target.value);
                if (errors.oldPassword) {
                  setErrors((prev) => ({ ...prev, oldPassword: "" }));
                }
              }}
              error={errors.oldPassword}
            />
          </div>
          <div className="Spacing-Bottom">
            <PasswordField
              title="New Password"
              placeholder="Enter new password"
              name="newPassword"
              id="newPassword"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (errors.newPassword || errors.confirmPassword) {
                  setErrors((prev) => ({
                    ...prev,
                    newPassword: "",
                    confirmPassword: "",
                  }));
                }
              }}
              error={errors.newPassword}
            />
          </div>
          <div className="Spacing-Bottom">
            <PasswordField
              title="Re-enter Password"
              placeholder="Re-enter new password"
              name="confirmPassword"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) {
                  setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                }
              }}
              error={errors.confirmPassword}
            />
          </div>
          <button
            type="submit"
            className={`Personal-Information-Save-Button${
              loading ? " Personal-Information-Save-Button--primary-loading" : ""
            }`}
            disabled={loading}
            style={{
              marginTop: "20px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : null}
            Save Changes
          </button>
        </Box>
      </Box>
    </div>
  );
}

export default Security;
